/**
 * 管理端 OpenTelemetry Web SDK RUM 采集初始化。
 *
 * document-load + fetch/xhr 自动埋点 + React Router 路由 span（pageCode 从路由 meta 读取注入），
 * 用户上下文 enduser.id[内部 ID,非手机号]/session.id/organizationId 登录设置/退出清空，
 * 不开 user-interaction 点击埋点；上报失败静默降级（BatchSpanProcessor 默认不抛，不影响业务）。
 * 覆盖 admin-web-shell:SC-21/SC-23/SC-24/SC-25。
 */
import { trace, type Span } from "@opentelemetry/api";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  type SpanProcessor,
} from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import type { SessionState } from "../auth/SessionContext";

/** 当前用户上下文镜像（登录设置/退出清空） */
let currentUser: SessionState | null = null;
/** session.id 上报值：登录令牌的哈希派生（不可反推出令牌原文，仅用于 trace 会话关联）。 */
let sessionHash = "";

/**
 * 非加密字符串哈希（FNV-1a 32bit ×2 拼 64bit hex）。
 * 登录令牌为高熵随机串，64bit 哈希不可反推原文；同步计算适配 SpanProcessor.onStart。
 */
function hashSessionId(token: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ (c + i), 0x01000193) >>> 0;
  }
  return h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0");
}

/**
 * 自定义 SpanProcessor：在每个 span 启动时注入用户上下文 attribute。
 * enduser.id 为内部员工 ID（1-5 桩；2-2 真实 UUID），MUST NOT 使用手机号；
 * session.id 为令牌哈希派生值，MUST NOT 上报令牌明文（attribute 白名单口径）。
 */
export class UserContextSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    if (currentUser) {
      span.setAttribute("enduser.id", currentUser.staffId);
      span.setAttribute("session.id", sessionHash);
      span.setAttribute("organizationId", currentUser.organizationId);
    }
  }
  onEnd(): void {
    /* no-op */
  }
  shutdown(): Promise<void> {
    return Promise.resolve();
  }
  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

let initialized = false;

/**
 * 初始化 OTel Web SDK。
 *
 * 采样率经 VITE_OTEL_SAMPLE_RATIO 下发（ParentBased + TraceIdRatioBased）；
 * OTLP 上报到 VITE_OTLP_ENDPOINT（后端 /otlp/v1/traces 代理）；
 * 不注册 UserInteractionInstrumentation（按 frontend-rum.md §3，动作级 span 随业务 Change 落地）。
 * 上报失败由 BatchSpanProcessor 静默吞掉，不影响业务交互与页面渲染。
 */
export function initOTel(): void {
  if (initialized) return;
  initialized = true;

  const endpoint = import.meta.env.VITE_OTLP_ENDPOINT ?? "/otlp/v1/traces";
  const ratio = Number(import.meta.env.VITE_OTEL_SAMPLE_RATIO ?? 1);

  const provider = new WebTracerProvider({
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(ratio),
    }),
    spanProcessors: [
      new UserContextSpanProcessor(),
      new BatchSpanProcessor(new OTLPTraceExporter({ url: endpoint })),
    ],
  });
  provider.register({ contextManager: new ZoneContextManager() });

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({ propagateTraceHeaderCorsUrls: [/.*/] }),
      new XMLHttpRequestInstrumentation(),
      // 不注册 UserInteractionInstrumentation（SC-25）
    ],
  });
}

/** 登录时设置用户上下文（enduser.id 为内部 ID，非手机号）。覆盖 admin-web-shell:SC-23 */
export function setSessionUser(s: SessionState): void {
  currentUser = s;
  sessionHash = hashSessionId(s.token);
}

/** 退出时清空用户上下文。覆盖 admin-web-shell:SC-23 */
export function clearSessionUser(): void {
  currentUser = null;
  sessionHash = "";
}

/** 当前未结束的 pageRoute span（路由切换时结束上一个，以记录上一视图停留时长）。 */
let currentRouteSpan: Span | undefined;

/** 懒取 Tracer，避免模块加载时缓存到尚未注册的 noop Provider。 */
function getRouteTracer() {
  return trace.getTracer("admin-web-route");
}

/**
 * 路由切换产生 pageRoute span，attribute 携带对应视图的 pageCode（对齐 ADM 编号）。
 *
 * 由路由/布局层在 pathname 或 pageCode 变化时调用；自动结束上一个 pageRoute span 以记录停留时长。
 * 覆盖 admin-web-shell:SC-21。
 */
export function startPageRouteSpan(pageCode: string): void {
  currentRouteSpan?.end();
  currentRouteSpan = getRouteTracer().startSpan("pageRoute", {
    attributes: { pageCode },
  });
}

/** 懒取动作 Tracer，原因同路由 Tracer。 */
function getActionTracer() {
  return trace.getTracer("admin-web-action");
}

/** 动作级 span 句柄（结束时上报）。 */
export interface ActionSpanHandle {
  end: () => void;
}

/**
 * 动作级遥测（frontend-rum §3：P0 资金/教务链路强制，命名 `action: <domain>.<verb>`）。
 *
 * 由页面在关键动作（如报名提交）发起时调用，完成后调用句柄 end()；
 * 本期不做页面内多动作并发管理（YAGNI），未结束句柄随进程退出丢弃。
 */
export function startAction(
  name: string,
  attributes?: Record<string, string | number | boolean>,
): ActionSpanHandle {
  const span = getActionTracer().startSpan(name, { attributes });
  return {
    end: () => span.end(),
  };
}
